import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Typography,
  Button,
  useMediaQuery,
  Box,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AddCircleOutlineOutlined,
  CancelRounded,
  Edit,
} from '@mui/icons-material';
import { useAccount } from '../../contexts/Account';
import categoryBenefitsMap from '../../utils/categoryBenefitsMap';
import CustomIcon from '../../icons/customIcon';
import ACButton from '../../components/ACButton';
import { benefitsUpdate } from '../../services/company_services';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import { SnackbarType } from '../../services/utils/types';

const useStyles = ({ isSM }) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: '80px',
  },
  text: {
    mx: '0px',
    px: { xxs: '60px', xs: '0px' },
  },
  listItem: {
    marginBottom: 12,
  },
  additionalBenefitsLeftContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  benefitsCategoryText: {
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '-0.43px',
    color: '#232B35',
    marginBottom: '11px',
    overflow: 'hidden',
  },
  button: {
    height: '20px',
    width: '20px',
    marginLeft: '3px',
  },
  iconbutton: {
    height: '17px',
    width: '17px',
  },
  actions: {
    minWidth: '46px',
  },
  mainButton: {
    width: '94px',
    height: '44px',
    borderRadius: '10px',
    letterSpacing: -0.43,
    fontStyle: 'normal',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '24px',
    backgroundColor: '#3171F6',
    color: 'white',
    marginLeft: '10px',
    marginTop: '10px',
    marginBottom: '10px',
    '&:hover': {
      backgroundColor: '#3171F6',
    },
    '&:disabled': {
      opacity: 0.5,
      backgroundColor: '#3171F6',
      color: 'white',
    },
  },
  secondaryButton: {
    width: '94px',
    height: '44px',
    letterSpacing: -0.43,
    fontStyle: 'normal',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '24px',
    color: '#646D7A',
    marginLeft: '10px',
    marginTop: '10px',
    marginBottom: '10px',
    '&:disabled': {
      opacity: 0.5,
      color: '#646D7A',
    },
  },
});

const INITIAL_BENEFITS = [
  {
    id: 'benefit-1',
    category: 'Advisory Board Affiliation',
    details:
      'Advisors may add their affiliation with this advisory board on their resume, biography and anywhere else',
    featured: false,
    order: 1,
  },
  {
    id: 'benefit-2',
    category: 'Complimentary Product',
    details:
      'Advisors receive a complimentary product, service or gift certificate for being on this board at some specified point.',
    featured: false,
    order: 2,
  },
  {
    id: 'benefit-3',
    category: 'Board Member Discount',
    details:
      'Advisors may receive a discount on particular products or services sold by this company.',
    featured: false,
    order: 3,
  },
  {
    id: 'benefit-4',
    category: 'Advisor Rate Opportunity',
    details:
      'Advisors may be hired at their specified Advisor Rate if their ideas shared in this digital boardroom warrant additional insights or an additional project.',
    featured: false,
    order: 4,
  },
  {
    id: 'benefit-5',
    category: '$1k Golden Idea Bonus',
    details:
      "If one of the ideas shared in this boardroom is deemed a “Golden Idea” by the board's leadership, they will receive an Advisor Bonus of $1,000",
    featured: false,
    order: 5,
  },
  {
    id: 'benefit-6',
    category: '1% Equity Pledge',
    details:
      'Active advisors may be granted 1% equity on your next capital raise. (AdvisoryCloud will help with all the administration.)',
    featured: false,
    order: 6,
  },
  {
    id: 'benefit-7',
    category: 'Perks',
    details:
      "Advisors may be invited to an event or special dinner hosted by the board's leadership, or a once a year meal or party you put on for your advisory board.",
    featured: false,
    order: 7,
  },
  {
    id: 'benefit-8',
    category: 'Perks',
    details:
      'Advisors may receive a hat, plaque, letter of recommendation on their behalf, or a coffee/beer the next time the board meets in person!',
    featured: false,
    order: 8,
  },
];

function BenefitCard({
  benefit,
  classes,
  formatDetails,
  getIconByText,
  index,
  isImpersonated,
  isXS,
  onEdit,
  onRemove,
  onToggleFeatured,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: benefit.id });

  const style = {
    border: '1px solid #EFF0F7',
    borderRadius: '12px',
    width: isXS ? '100%' : '330px',
    padding: '20px',
    margin: isXS ? '10px 5px' : '14px 15px 14px 0px',
    backgroundColor: '#fff',
    boxShadow: isDragging ? '0px 12px 24px rgba(35, 43, 53, 0.16)' : 'none',
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
  };

  return (
    <div
      className="benefitItem"
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div style={classes.additionalBenefitsLeftContainer}>
        <div
          style={{
            marginRight: '15px',
            marginTop: '3px',
          }}
        >
          {getIconByText(benefit.category)}
        </div>
        <Typography sx={classes.benefitsCategoryText}>
          {benefit.category}
        </Typography>
        <div style={classes.actions}>
          <div>
            <IconButton
              sx={classes.button}
              onClick={() => {
                onEdit(benefit, index);
              }}
            >
              <Edit sx={classes.iconbutton} />
            </IconButton>
            <IconButton
              sx={classes.button}
              onClick={() => {
                onRemove(index);
              }}
            >
              <CancelRounded sx={classes.iconbutton} />
            </IconButton>
            {isImpersonated && (
              <IconButton
                sx={classes.button}
                onClick={() => {
                  onToggleFeatured(index);
                }}
              >
                <CustomIcon
                  iconname={benefit.featured ? 'star' : 'star-outlined'}
                  style={{
                    fontSize: '18px',
                    marginTop: '-3px',
                  }}
                />
              </IconButton>
            )}
          </div>
        </div>
      </div>
      <div style={classes.additionalBenefitsLeftContainer}>
        <Box sx={{ width: '100%' }}>
          <Typography sx={classes.benefitsDetailsText}>
            {formatDetails(benefit.details)}
          </Typography>
        </Box>
      </div>
    </div>
  );
}

export default function step3() {
  const history = useHistory();
  const { accountDetails, asLoggedUserDetails, isImpersonated } = useAccount();
  const [benefits, setBenefits] = useState(() =>
    INITIAL_BENEFITS.map((benefit) => ({ ...benefit }))
  );
  const benefitIdCounter = useRef(INITIAL_BENEFITS.length);
  const [addBenefitModal, setAddBenefitModal] = useState(false);
  const [category, setCategory] = useState('');
  const [details, setDetails] = useState('');
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles({ isSM });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    setBenefits((prevBenefits) => {
      const oldIndex = prevBenefits.findIndex(
        (benefit) => benefit.id === active.id
      );
      const newIndex = prevBenefits.findIndex(
        (benefit) => benefit.id === over.id
      );

      if (oldIndex === -1 || newIndex === -1) {
        return prevBenefits;
      }

      const reorderedBenefits = arrayMove(prevBenefits, oldIndex, newIndex);

      return reorderedBenefits.map((benefit, index) => ({
        ...benefit,
        order: index + 1,
      }));
    });
  };

  const openEditBenefit = (benefit, index) => {
    setAddBenefitModal(true);
    setCategory(benefit.category);
    setDetails(benefit.details);
    setSelectedBenefit(index);
  };

  const featuredBenefit = (index) => {
    setBenefits((prevBenefits) => {
      const updatedBenefits = [...prevBenefits];
      updatedBenefits[index] = {
        ...updatedBenefits[index],
        featured: !updatedBenefits[index].featured,
      };
      return updatedBenefits;
    });
  };

  const removeBenefit = (index) => {
    setBenefits((prevBenefits) => {
      const filteredBenefits = prevBenefits.filter((_, i) => i !== index);
      return filteredBenefits.map((benefit, benefitIndex) => ({
        ...benefit,
        order: benefitIndex + 1,
      }));
    });
  };

  function addNewBenefit(benefit) {
    setBenefits((prevBenefits) => {
      if (typeof selectedBenefit === 'number') {
        const updatedBenefits = [...prevBenefits];
        updatedBenefits[selectedBenefit] = {
          ...updatedBenefits[selectedBenefit],
          ...benefit,
        };
        return updatedBenefits;
      }
      benefitIdCounter.current += 1;
      return [
        ...prevBenefits,
        {
          id: `benefit-${benefitIdCounter.current}`,
          featured: false,
          order: prevBenefits.length + 1,
          ...benefit,
        },
      ];
    });
  }

  function getIconByText(text) {
    const option =
      categoryBenefitsMap[text] || categoryBenefitsMap[text.toLowerCase()];
    return option ? option.icon : '';
  }

  function formatDetails(text) {
    var urlPattern = /(https?:\/\/[^\s]+)/g;
    var cleanedText = text.replace(urlPattern, '');

    cleanedText = cleanedText.substring(0, 250);

    return cleanedText;
  }

  async function handleSubmit() {
    try {
      setIsLoading(true);
      await benefitsUpdate({
        CONTACT_ID: asLoggedUserDetails.userId,
        COMPANY_BENEFITS: benefits.map(({ id, ...benefit }) =>
          JSON.stringify(benefit)
        ),
      });
      history.push('/company/onboarding/step4');
    } catch (error) {
      console.log('Error updating benefits:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box sx={classes.content}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          // maxWidth: '700px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: isXS ? '24px' : '28px',
            fontWeight: 700,
            letterSpacing: '-0.43px',
            textAlign: 'center',
            my: '12px',
          }}
        >
          What perks would you like to offer your advisory board?
        </Typography>
        <Typography
          sx={{
            fontStyle: 'italic',
            fontSize: '16px',
            fontWeight: 300,
            lineHeight: '24px',
            letterSpacing: '-0.43px',
            color: '#646D7A',
            marginBottom: '18px',
          }}
        >
          The more perks, the more engaged they'll be! Use or edit the examples
          below, or add your own. You can edit them or add more at anytime in
          your boardroom.
        </Typography>
      </Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={benefits.map((benefit) => benefit.id)}
          strategy={rectSortingStrategy}
        >
          <Grid
            container
            style={{
              marginBottom: '10px',
              justifyContent: 'center',
              maxWidth: '1035px',
            }}
          >
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={benefit.id}
                benefit={benefit}
                classes={classes}
                formatDetails={formatDetails}
                getIconByText={getIconByText}
                index={index}
                isImpersonated={isImpersonated}
                isXS={isXS}
                onEdit={openEditBenefit}
                onRemove={removeBenefit}
                onToggleFeatured={featuredBenefit}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      {addBenefitModal ? (
        <Box>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ marginTop: '5px', marginBottom: '8px' }}>
              <Typography>
                Choose any benefits or compensation opportunities for advisors
              </Typography>
            </Grid>
          </Grid>
          <FormControl fullWidth>
            <InputLabel
              sx={{
                height: '44px',
                top: '-4px',
                fontFamily: 'Poppins',
                display: !category ? 'block' : 'none',
              }}
              id="demo-simple-select-label"
              variant="filled"
            >
              Select a benefits category
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={category || ''}
              placeholder="Select a benefits category"
              sx={{
                height: '44px',
                borderRadius: '10px',
              }}
              onChange={(e) => {
                setCategory(e.target.value);
              }}
            >
              {Object.entries(categoryBenefitsMap).map(
                ([key, { icon, label }]) => (
                  <MenuItem sx={classes.menuItem} key={label} value={label}>
                    {label}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <div>
            <TextField
              placeholder="A short description of your benefit is required. e.g. Offering equity awards for select members of our advisory board"
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              style={{
                marginRight: 10,
                marginTop: 15,
              }}
              value={details || ''}
              inputProps={{
                maxLength: 250,
                style: {
                  borderRadius: '10px',
                },
              }}
              onChange={(evt) => setDetails(evt.target.value)}
            />
          </div>
          <Grid container justifyContent="flex-end">
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  addNewBenefit({ category, details });
                  setCategory('');
                  setDetails('');
                  setAddBenefitModal(false);
                  setSelectedBenefit(null);
                }}
                sx={classes.mainButton}
                disabled={!category || !details || details.length < 10}
              >
                Save
              </Button>
            </Box>
            <Box>
              <Button
                variant="text"
                color="primary"
                onClick={() => {
                  setCategory('');
                  setDetails('');
                  setAddBenefitModal(false);
                  setSelectedBenefit(null);
                }}
                sx={classes.secondaryButton}
              >
                Discard
              </Button>
            </Box>
          </Grid>
        </Box>
      ) : null}
      {!addBenefitModal && (
        <div style={classes.addContentContainer}>
          <ACButton
            color="primary"
            size="medium"
            variant="text"
            style={{
              borderWidth: 2,
              borderRadius: 5,
              paddingLeft: 0,
            }}
            onClick={() => {
              setAddBenefitModal(true);
            }}
            disabled={isLoading}
          >
            <AddCircleOutlineOutlined />
            <Typography>Add a benefit</Typography>
          </ACButton>
        </div>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '28px',
        }}
      >
        <Button
          id="company-onboarding-step-3"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          className="getStartedButton"
          disabled={isLoading}
          sx={{
            paddingTop: '15px',
            paddingBottom: '15px',
            textTransform: 'none',
            fontWeight: '700',
            fontSize: '15px',
            width: '260px',
            height: '44px',
            borderRadius: '30px',
          }}
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Finish!'}
        </Button>
      </Box>
      <ACSnackbar
        open={showSnackbar}
        text="Something went wrong"
        severity={SnackbarType.ERROR}
        onClose={() => {
          setShowSnackbar(false);
        }}
        autoHideDuration={5000}
      />
    </Box>
  );
}
